import React from 'react';
import { SortableTh, useTableSort } from '../weeklyTableHelpers';
import { KM_BANDS, PER_SEAT_ROWS, rowTotal } from './invoicingSurface';

/**
 * Per Seat - Tiered invoicing sub-table: four rows across the nine KM bands, each closing
 * with that row's own total. Mirrors the Tiered CSV this row's download button produces, so
 * the screen and the file can be diffed figure for figure.
 *
 * Counts only — no rand value anywhere. The company's seat rates live on its rate card and
 * Xero multiplies them out, which is why the legacy table's two fee columns have no
 * counterpart here.
 *
 * @param {Object} props
 * @param {Object} props.perSeat - `toggle_table.per_seat`, keyed by row then by band.
 * @param {number|string} props.companyId - Used only for testids.
 * @returns {JSX.Element}
 */
const TieredPerSeatTable = ({ perSeat, companyId }) => {
  // All four rows are built even when a row is empty. A company that ran no ad-hoc bookings
  // this week gets a zeroed Ad Hoc Trip row, not a missing one: the rows are reconciled
  // against the previous period, and a row that disappears reads as a broken view.
  const rows = PER_SEAT_ROWS.map((row) => {
    const counts = perSeat?.[row.key] || {};

    return {
      key: row.key,
      trip_type: row.label,
      ...KM_BANDS.reduce((acc, band) => ({ ...acc, [band.key]: counts[band.key] ?? 0 }), {}),
      total: rowTotal(counts),
    };
  });

  // Flat rows, so sort by direct property access rather than dot-path — same as the legacy
  // table's vehicle rows.
  const { sortConfig, handleSort, sortedRows } = useTableSort(rows, { getValue: (row, key) => row[key] });

  return (
    <table className="table-fixed w-full border-collapse" data-testid={`invoicing-subtable-${companyId}`}>
      <thead>
        <tr>
          <SortableTh
            label="TripType"
            sortKey="trip_type"
            sortConfig={sortConfig}
            onSort={handleSort}
            testId={`invoicing-subth-trip_type-${companyId}`}
          />
          {KM_BANDS.map((band) => (
            <SortableTh
              key={band.key}
              label={band.label}
              sortKey={band.key}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="center"
              testId={`invoicing-subth-${band.key}-${companyId}`}
            />
          ))}
          {/* Deliberately the last column and deliberately a ROW total. There is no column
              footer: reading this column downwards would add bookings to rides. */}
          <SortableTh
            label="Total"
            sortKey="total"
            sortConfig={sortConfig}
            onSort={handleSort}
            align="center"
            testId={`invoicing-subth-total-${companyId}`}
          />
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row) => (
          <tr key={row.key}>
            <td
              className="text-gray-700 font-bold border-t border-b p-2 text-left"
              data-testid={`invoicing-subtd-${row.key}-label-${companyId}`}
            >
              {row.trip_type}
            </td>
            {KM_BANDS.map((band) => (
              <td
                key={band.key}
                className="text-gray-700 border-t border-b p-2 text-center"
                data-testid={`invoicing-subtd-${row.key}-${band.key}-${companyId}`}
              >
                {row[band.key]}
              </td>
            ))}
            <td
              className="text-gray-700 font-bold border-t border-b p-2 text-center"
              data-testid={`invoicing-subtd-${row.key}-total-${companyId}`}
            >
              {row.total}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default TieredPerSeatTable;
